import { ArrowLeft, ArrowUpRight, Loader2, Trash2 } from "lucide-react";
import StatusDot from "@/components/common/StatusDot";
import { toneSurface } from "@/lib/mockData";
import { cn } from "@/lib/utils";

export default function ChatHeader({
  agent,
  onForward,
  forwarding,
  onClear,
  canClear,
  onBack,
}) {
  const tone = toneSurface[agent.tone] ?? toneSurface.neutral;
  const isDarkAvatar = agent.tone === "dark";

  return (
    <div
      data-testid="chat-header"
      className="border-b border-black/[0.04] bg-[#F7F7F5]/85 px-5 py-4 backdrop-blur sm:px-8 sm:py-5 lg:px-12"
    >
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-6">
        <div className="flex w-full items-center gap-3 sm:gap-4">
          {/* Mobile-only back button */}
          {onBack && (
            <button
              type="button"
              data-testid="chat-back-btn"
              onClick={onBack}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-black/[0.06] bg-white text-zinc-700 transition-colors hover:bg-zinc-50 md:hidden"
              aria-label="Back to agent list"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2.4} />
            </button>
          )}
          <div
            className={cn(
              "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-[15px] font-extrabold sm:h-12 sm:w-12 sm:text-[16px]",
              isDarkAvatar
                ? "bg-[#0B0B0E] text-white"
                : cn(tone.bg, "text-zinc-900"),
            )}
            style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
          >
            {agent.name[0]}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1
                data-testid="chat-header-name"
                className="text-[18px] font-extrabold tracking-tight text-zinc-900 sm:text-[24px]"
              >
                {agent.name}
              </h1>
              <span className="rounded-full border border-black/[0.06] bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-700">
                {agent.role}
              </span>
              <StatusDot status={agent.status} />
            </div>
            <div className="mt-1 truncate text-[12px] text-zinc-500 sm:text-[12.5px]">
              {agent.focus}
            </div>
          </div>
        </div>

        <div className="flex w-full flex-shrink-0 items-center gap-2 sm:w-auto">
          {canClear && (
            <button
              type="button"
              data-testid="chat-clear"
              onClick={onClear}
              className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-full border border-black/[0.06] bg-white px-3 py-2 text-[12px] font-semibold text-zinc-600 transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 sm:flex-initial"
              aria-label="Clear conversation"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={2.4} />
              Clear
            </button>
          )}
          <button
            type="button"
            data-testid="send-to-boardroom"
            onClick={onForward}
            disabled={forwarding}
            className="group inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-full bg-black px-4 py-2 text-[12px] font-semibold text-white transition-all hover:bg-zinc-800 disabled:opacity-60 sm:flex-initial sm:text-[12.5px]"
          >
            {forwarding ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="hidden sm:inline">Forwarding…</span>
                <span className="sm:hidden">…</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Send to Boardroom</span>
                <span className="sm:hidden">Boardroom</span>
                <ArrowUpRight
                  className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  strokeWidth={2.4}
                />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
